import { useEffect, useState, useCallback } from 'react'
import {
  SystemSkillCategory,
  SystemSkillItem,
  listSystemCategories,
  listSystemItems,
  createSystemCategory,
  updateSystemCategory,
  deleteSystemCategory,
  createSystemItem,
  updateSystemItem,
  deleteSystemItem,
} from '@/services/admin_skills'
import { LexicalMarkdownEditor } from '@/components/editor/LexicalMarkdownEditor'

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
)

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const FolderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
)

export function SystemSkillsTab() {
  const [categories, setCategories] = useState<SystemSkillCategory[]>([])
  const [items, setItems] = useState<SystemSkillItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<SystemSkillItem | null>(null)

  // Category modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<SystemSkillCategory | null>(null)
  const [categoryName, setCategoryName] = useState('')
  const [categoryIcon, setCategoryIcon] = useState('')

  // Right panel inline editing state
  const [isEditing, setIsEditing] = useState(false)
  const [editingItem, setEditingItem] = useState<SystemSkillItem | null>(null)
  const [itemName, setItemName] = useState('')
  const [itemSummary, setItemSummary] = useState('')
  const [itemContent, setItemContent] = useState('')
  const [itemCategoryId, setItemCategoryId] = useState('')
  const [saving, setSaving] = useState(false)

  const reload = async () => {
    setLoading(true)
    try {
      const [catsRes, itemsRes] = await Promise.all([
        listSystemCategories(),
        listSystemItems(),
      ])
      setCategories(catsRes.items || [])
      setItems(itemsRes.items || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  const filteredItems = selectedCategory
    ? items.filter(i => i.category_id === selectedCategory)
    : items

  // Category CRUD
  const openCreateCategory = () => {
    setEditingCategory(null)
    setCategoryName('')
    setCategoryIcon('')
    setShowCategoryModal(true)
  }

  const openEditCategory = (cat: SystemSkillCategory) => {
    setEditingCategory(cat)
    setCategoryName(cat.name)
    setCategoryIcon(cat.icon || '')
    setShowCategoryModal(true)
  }

  const submitCategory = async () => {
    const name = categoryName.trim()
    if (!name) return
    try {
      if (editingCategory) {
        await updateSystemCategory(editingCategory.id, { name, icon: categoryIcon || null })
      } else {
        await createSystemCategory({ name, icon: categoryIcon || null })
      }
      setShowCategoryModal(false)
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to save category')
    }
  }

  const handleDeleteCategory = async (cat: SystemSkillCategory) => {
    if (!confirm(`Delete category "${cat.name}" and all its skills?`)) return
    try {
      await deleteSystemCategory(cat.id)
      if (selectedCategory === cat.id) setSelectedCategory(null)
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to delete')
    }
  }

  // Skill inline editing in right panel
  const startCreateItem = () => {
    setEditingItem(null)
    setSelectedItem(null)
    setItemName('')
    setItemSummary('')
    setItemContent('# Skill Title\n\n## Summary\nDescribe the skill here.\n\n## Pro tip\nAdd practical advice.')
    setItemCategoryId(selectedCategory || (categories[0]?.id || ''))
    setIsEditing(true)
  }

  const startEditItem = (item: SystemSkillItem) => {
    setEditingItem(item)
    setSelectedItem(item)
    setItemName(item.name)
    setItemSummary(item.summary || '')
    setItemContent(item.content_md)
    setItemCategoryId(item.category_id)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setEditingItem(null)
  }

  const submitItem = async () => {
    const name = itemName.trim()
    const content = itemContent.trim()
    if (!name || !content || !itemCategoryId) return
    setSaving(true)
    try {
      if (editingItem) {
        await updateSystemItem(editingItem.id, {
          name,
          summary: itemSummary || undefined,
          content_md: content,
          category_id: itemCategoryId,
        })
      } else {
        await createSystemItem({
          category_id: itemCategoryId,
          name,
          summary: itemSummary || undefined,
          content_md: content,
        })
      }
      setIsEditing(false)
      setEditingItem(null)
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to save skill')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteItem = async (item: SystemSkillItem) => {
    if (!confirm(`Delete skill "${item.name}"?`)) return
    try {
      await deleteSystemItem(item.id)
      if (selectedItem?.id === item.id) {
        setSelectedItem(null)
        setIsEditing(false)
        setEditingItem(null)
      }
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to delete')
    }
  }

  const selectedCategoryObj = categories.find(c => c.id === selectedCategory)

  // Resizable right panel
  const [rightWidth, setRightWidth] = useState(480)
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = rightWidth
    const onMouseMove = (ev: MouseEvent) => {
      const newWidth = Math.min(Math.max(startWidth + (startX - ev.clientX), 320), 800)
      setRightWidth(newWidth)
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [rightWidth])

  return (
    <>
      <div className="admin-skills-layout" style={{ gridTemplateColumns: `220px minmax(0, 1fr) ${rightWidth}px` }}>
        {/* Left: Categories */}
        <div className="admin-skills-panel">
          <div className="admin-skills-panel-header">
            <h3>Categories</h3>
            <button className="admin-btn-icon" onClick={openCreateCategory} title="Add category">
              <PlusIcon />
            </button>
          </div>
          <div className="admin-skills-panel-body">
            <div
              className={`admin-skills-cat-item ${!selectedCategory ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              <div className="admin-skills-cat-left">
                <div className="admin-skills-cat-icon"><FolderIcon /></div>
                <span className="admin-skills-cat-name">All Skills</span>
              </div>
              <div className="admin-skills-cat-right">
                <span className="admin-skills-cat-count">{items.length}</span>
              </div>
            </div>
            {categories.map(cat => (
              <div
                key={cat.id}
                className={`admin-skills-cat-item ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                <div className="admin-skills-cat-left">
                  <div className="admin-skills-cat-icon">
                    {cat.icon ? (
                      <svg
                        className="icon"
                        style={cat.icon === 'twitter' || cat.icon === 'linkedin' || cat.icon === 'reddit' ? { fill: 'currentColor', stroke: 'none' } : {}}
                      >
                        <use href={`#icon-${cat.icon}`} />
                      </svg>
                    ) : <FolderIcon />}
                  </div>
                  <span className="admin-skills-cat-name">{cat.name}</span>
                </div>
                <div className="admin-skills-cat-right">
                  <span className="admin-skills-cat-count">{cat.item_count}</span>
                  <div className="admin-skills-cat-actions">
                    <button className="admin-btn-icon" onClick={(e) => { e.stopPropagation(); openEditCategory(cat) }}>
                      <EditIcon />
                    </button>
                    <button className="admin-btn-icon admin-btn-icon--danger" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat) }}>
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Middle: Items */}
        <div className="admin-skills-panel">
          <div className="admin-skills-panel-header">
            <h3>{selectedCategoryObj ? selectedCategoryObj.name : 'All Skills'} ({filteredItems.length})</h3>
            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={startCreateItem} style={{ width: 'auto' }}>
              <PlusIcon /> Add Skill
            </button>
          </div>
          <div className="admin-skills-panel-body">
            {loading && <div className="admin-loading">Loading...</div>}
            {!loading && filteredItems.length === 0 && (
              <div className="admin-empty">No skills yet. Click "Add Skill" to create one.</div>
            )}
            {filteredItems.map(item => (
              <div
                key={item.id}
                className={`admin-skills-skill-item ${selectedItem?.id === item.id ? 'active' : ''}`}
                onClick={() => { setSelectedItem(item); setIsEditing(false); setEditingItem(null) }}
              >
                <div className="admin-skills-skill-left">
                  <div className="admin-skills-skill-name">{item.name}</div>
                  {item.summary && (
                    <div className="admin-skills-skill-summary">{item.summary}</div>
                  )}
                  {!selectedCategory && item.category_name && (
                    <div className="admin-skills-skill-meta">
                      <span className="admin-skills-skill-tag">
                        {item.category_icon && (
                          <svg
                            className="icon"
                            style={{
                              width: 12,
                              height: 12,
                              marginRight: 4,
                              ...(item.category_icon === 'twitter' || item.category_icon === 'linkedin' || item.category_icon === 'reddit'
                                ? { fill: 'currentColor', stroke: 'none' }
                                : {}),
                            }}
                          >
                            <use href={`#icon-${item.category_icon}`} />
                          </svg>
                        )}
                        {item.category_name}
                      </span>
                    </div>
                  )}
                </div>
                <div className="admin-skills-skill-actions">
                  <button className="admin-btn-icon" onClick={(e) => { e.stopPropagation(); startEditItem(item) }}>
                    <EditIcon />
                  </button>
                  <button className="admin-btn-icon admin-btn-icon--danger" onClick={(e) => { e.stopPropagation(); handleDeleteItem(item) }}>
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Detail / Edit panel */}
        <div className="admin-skills-panel admin-skills-panel--right">
          <div className="admin-skills-resize-handle" onMouseDown={handleResizeMouseDown} />
          {isEditing ? (
            <>
              <div className="admin-skills-detail-header">
                <div className="admin-skills-detail-title">
                  {editingItem ? 'Edit Skill' : 'New Skill'}
                </div>
              </div>
              <div className="admin-skills-detail-body">
                <div className="admin-skills-edit-form">
                  <div className="admin-skills-edit-row">
                    <div className="admin-form-group" style={{ flex: 1 }}>
                      <label>Name</label>
                      <input
                        className="admin-input"
                        value={itemName}
                        onChange={e => setItemName(e.target.value)}
                        placeholder="e.g. Inverted Pyramid"
                        autoFocus
                      />
                    </div>
                    <div className="admin-form-group" style={{ width: 140 }}>
                      <label>Category</label>
                      <select
                        className="admin-input"
                        value={itemCategoryId}
                        onChange={e => setItemCategoryId(e.target.value)}
                      >
                        <option value="">Select...</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="admin-form-group">
                    <label>Summary</label>
                    <input
                      className="admin-input"
                      value={itemSummary}
                      onChange={e => setItemSummary(e.target.value)}
                      placeholder="Brief description for user display"
                    />
                  </div>
                  <div className="admin-form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <label>Content (Markdown)</label>
                    <div className="admin-skills-editor-fill">
                      <LexicalMarkdownEditor
                        key={editingItem ? `edit-${editingItem.id}` : 'new'}
                        initialMarkdown={itemContent}
                        onMarkdownChange={setItemContent}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-skills-detail-actions">
                <button className="admin-btn admin-btn--sm" onClick={cancelEdit}>Cancel</button>
                <button
                  className="admin-btn admin-btn--sm admin-btn--primary"
                  style={{ width: 'auto' }}
                  onClick={submitItem}
                  disabled={saving || !itemName.trim() || !itemCategoryId}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          ) : selectedItem ? (
            <>
              <div className="admin-skills-detail-header">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="admin-skills-detail-title">{selectedItem.name}</div>
                  <button className="admin-btn-icon" onClick={() => startEditItem(selectedItem)} title="Edit">
                    <EditIcon />
                  </button>
                </div>
                <div className="admin-skills-detail-subtitle">{selectedItem.category_name}</div>
              </div>
              <div className="admin-skills-detail-body">
                {selectedItem.summary && (
                  <div className="admin-skills-detail-summary">
                    {selectedItem.summary}
                  </div>
                )}
                <div className="admin-skills-editor-fill">
                  <LexicalMarkdownEditor
                    key={`preview-${selectedItem.id}`}
                    initialMarkdown={selectedItem.content_md || ''}
                    onMarkdownChange={() => {}}
                    readOnly
                  />
                </div>
              </div>
              <div className="admin-skills-detail-actions">
                <button className="admin-btn admin-btn--sm" onClick={() => startEditItem(selectedItem)}>
                  Edit
                </button>
                <button className="admin-btn admin-btn--sm" style={{ color: 'var(--danger)' }} onClick={() => handleDeleteItem(selectedItem)}>
                  Delete
                </button>
              </div>
            </>
          ) : (
            <div className="admin-empty" style={{ flex: 1 }}>
              <div style={{ textAlign: 'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 12 }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                <div>Select a skill to preview</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category Modal (kept as modal since it's small) */}
      {showCategoryModal && (
        <div className="admin-modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'New Category'}</h3>
              <button className="admin-modal-close" onClick={() => setShowCategoryModal(false)}>&times;</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label>Name</label>
                <input
                  className="admin-input"
                  value={categoryName}
                  onChange={e => setCategoryName(e.target.value)}
                  placeholder="e.g. Blog, Twitter"
                  autoFocus
                />
              </div>
              <div className="admin-form-group">
                <label>Icon (optional)</label>
                <input
                  className="admin-input"
                  value={categoryIcon}
                  onChange={e => setCategoryIcon(e.target.value)}
                  placeholder="e.g. blog, twitter, linkedin"
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn" onClick={() => setShowCategoryModal(false)}>Cancel</button>
              <button className="admin-btn admin-btn--primary" style={{ width: 'auto' }} onClick={submitCategory}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
